import firebase from "firebase/app";
import React, {
  createContext,
  PropsWithChildren,
  useEffect,
  useState
} from "react";
import { auth, db, functions } from "../firebase";
import { takePendingLoginLink } from "../utils/pendingLoginLink";
import { Customer } from "../models/Customer";
import { RangeLocation } from "../models/RangeLocation";

export interface UserContextProps extends PropsWithChildren<{}> {
  user: firebase.User | null;
  customer: Customer | null;
  rangeLocation: RangeLocation | null;
  authenticated: boolean;
  setUser: React.Dispatch<React.SetStateAction<firebase.User | null>>;
  loadingAuthState: boolean;
  setCustomer: React.Dispatch<React.SetStateAction<Customer | null>>;
  loadingCustomerState: boolean;
  setRangeLocation: React.Dispatch<React.SetStateAction<RangeLocation | null>>;
  notice: string | null;
  setNotice: React.Dispatch<React.SetStateAction<string | null>>;
}

const attachPendingLogin = async (uid: string): Promise<string | null> => {
  const pending = takePendingLoginLink(uid);
  if (!pending) return null;
  const what = pending.kind === "phone" ? "phone number" : "email";
  try {
    const result = await functions.httpsCallable("attachLogin")({ proofToken: pending.token });
    return result.data && result.data.added
      ? `Your ${what} was added. You can now sign in with either your email or your phone.`
      : null;
  } catch (e) {
    const reason = e && (e as any).details && (e as any).details.reason;
    if (reason === `already-has-${pending.kind}`) {
      return `Your account already has a different ${what}, so we couldn't add this one. Keep signing in the way you just did.`;
    }
    if (reason === "proof-expired") {
      return `That took too long, so we couldn't add your ${what}. Sign in with it again and follow the same steps.`;
    }
    return `We couldn't add your ${what}. Keep signing in the way you just did.`;
  }
};

export const UserContext = createContext<Partial<UserContextProps>>({
  user: null,
  customer: null
});

export default function UserProvider(props: PropsWithChildren<{}>) {
  const [user, setUser] = useState<firebase.User | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  // const [phone, setPhone] = useState<string | null>(null);
  // const [email, setEmail] = useState<string | null>(null);
  const [loadingAuthState, setLoadingAuthState] = useState(true);
  const [loadingCustomerState, setLoadingCustomerState] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);
  // const [rangeLocation, setRangeLocation] = useState<RangeLocation | null>(
  //   null
  // );


  // Connecting Context
  // const [customerConnected, setCustomerConnected] = useState<boolean>(false);

  useEffect(() => {
    auth.onAuthStateChanged((user) => {
      setUser(user);
      setLoadingAuthState(false);
    });
  }, []);

  useEffect(() => {
    const getCustomerByUid = async () => {
      if (user?.uid) {
        const snapshot = await db
          .ref("/customers")
          .orderByChild("uid")
          .equalTo(user.uid)
          .limitToFirst(1)
          .once("value");

        if (snapshot.exists()) {
          attachPendingLogin(user.uid).then((message) => message && setNotice(message));
          const customerSnapshot = snapshot.toJSON()! as { [key: string]: Customer };

          // Use the record's own key: about a third of records have no `customer` field, and reading it
          // here used to load /customers/undefined (another person's data) as this customer's account.
          for (let customerKey of Object.keys(customerSnapshot)) {
            const customerRef = db.ref(`/customers/${customerKey}`);

            customerRef.on("value", async (snapshot) => {
              const customer = snapshot.val();
              if (!customer) return;
              customer.customer = customerKey;

              if (customer.activeRegistration) {
                const registration = await db
                  .ref(`/registrations/${customer.activeRegistration}`)
                  .once("value");

                const { location, queueId } = registration.val();

                customer.activeRegistration = {
                  id: registration.key!,
                  location,
                  queueId
                };
              }

              setCustomer(customer);
              setLoadingCustomerState(false);
            });
          }
        } else {
          setLoadingCustomerState(false);
        }
      }
    };

    getCustomerByUid();
  }, [user]);

  // useEffect(() => {
  //   if (user?.email != null) {
  //     setEmail(user.email);
  //   }
  // }, [user]);

  // useEffect(() => {
  //   if (phone) {
  //     db.ref("/customers")
  //       .orderByChild("phone")
  //       .equalTo(phone)
  //       .limitToFirst(1)
  //       .once("value")
  //       .then((snapshot) => {
  //         snapshot.forEach((s) => setCustomer(s.val() as Customer));
  //       });
  //   }
  // }, [phone]);

  // useEffect(() => {
  //   if (email) {
  //     db.ref("/customers")
  //       .orderByChild("email")
  //       .equalTo(email)
  //       .limitToFirst(1)
  //       .once("value")
  //       .then((snapshot) => {
  //         snapshot.forEach((s) => setCustomer(s.val() as Customer));
  //       });
  //   }
  // }, [email]);

  return (
    <UserContext.Provider
      value={{
        user,
        authenticated: user !== null,
        setUser,
        loadingAuthState,
        customer,
        setCustomer,
        loadingCustomerState,
        notice,
        setNotice
      }}
    >
      {props.children}
    </UserContext.Provider>
  );
}
